import { useEffect, useState, useMemo, useRef } from "react";
import { api } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot, Legend } from "recharts";
import { useT, getLocale } from "@/lib/i18n";
import { fuelAxis, hasSolar } from "@/lib/sensitivityFuel";
import { num, money, moneyM, pct, unit, perUnit, perYr } from "@/lib/numfmt";

const PCTS = [-30, -20, -10, 0, 10, 20, 30];

/**
 * Sweeps fuel_price and irradiance around baseline (−30% … +30%, 7 pts each) and
 * charts the LCOE curve. Called once on mount and again if the baseline changes.
 */
export const SensitivityChart = ({ baselineInput, baselineResult, currentFuel, currentIrr }) => {
  const t = useT();
  // Prompt K §8.7 — same author as the panel.
  const axis = useMemo(() => fuelAxis(baselineInput, baselineResult), [baselineInput, baselineResult]);
  const baselineFuel = axis.baseline;
  const baselineIrr = baselineInput?.irradiance ?? 5.5;
  const baselineLcoe = baselineResult?.financials?.lcoe_usd_per_kwh || 0;
  const [fuelSweep, setFuelSweep] = useState([]);
  const [irrSweep, setIrrSweep] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fuelFailed, setFuelFailed] = useState(false);
  const [irrFailed, setIrrFailed] = useState(false);
  const fr = getLocale() === "fr";

  // D3 also-check — the effect used to list `baselineInput`, `baselineResult`
  // and `axis` (a fresh object each time either of those changes identity) in
  // its deps, so any parent re-render re-fired all 14 `/configurator/run`
  // posts and `loading` never settled: the chart sat on "loading…" with no
  // curves. Keyed on a serialised baseline instead; the live objects are read
  // through a ref so they are never a dependency.
  const solar = hasSolar(baselineResult);
  const liveRef = useRef({ baselineInput, baselineResult, axis });
  liveRef.current = { baselineInput, baselineResult, axis };
  const sweepKey = JSON.stringify([baselineFuel, baselineIrr, axis.legendKey, baselineInput, solar,
    baselineLcoe, axis.runValue(baselineResult), baselineResult?.inputs_echo?.irradiance]);

  useEffect(() => {
    let cancelled = false;
    const { baselineInput: bi, baselineResult: br, axis: ax } = liveRef.current;
    const fuelPts = PCTS.map((p) => Number((baselineFuel * (1 + p / 100)).toFixed(3)));
    const irrPts = PCTS.map((p) => Number((baselineIrr * (1 + p / 100)).toFixed(2)));
    // a38 — the 0 % point is the run itself when its resolved fuel/irradiance match exactly.
    const runLcoe = br?.financials?.lcoe_usd_per_kwh;
    const hasRun = typeof runLcoe === "number";
    const fuelZero = hasRun && ax.runValue(br) === fuelPts[3];
    const irrZero = hasRun && br?.inputs_echo?.irradiance === irrPts[3];
    let calls = 0;
    const post = (body) => { calls += 1; return api.post("/configurator/run", body); };
    // a37 — each point retries once; a curve that still fails says so.
    const lcoeOf = (body) => post(body).catch(() => post(body)).then((r) => r.data.financials.lcoe_usd_per_kwh);
    const sweep = (pts, zero, bodyOf) => Promise.all(pts.map((v, i) => (i === 3 && zero ? runLcoe : lcoeOf(bodyOf(v)))))
      .then((res) => pts.map((v, i) => ({ pct: PCTS[i], x: v, lcoe: res[i] })));
    const t0 = Date.now();
    setLoading(true);
    setFuelFailed(false);
    setIrrFailed(false);
    const fuelP = sweep(fuelPts, fuelZero, (v) => ax.patch(bi, v)).then(
      (rows) => { if (!cancelled) setFuelSweep(rows); },
      (err) => { console.warn("[sens-chart] fuel curve failed", err?.message); if (!cancelled) { setFuelSweep([]); setFuelFailed(true); } },
    );
    const irrP = !solar ? Promise.resolve(setIrrSweep([])) : sweep(irrPts, irrZero, (v) => ({ ...bi, irradiance: v })).then(
      (rows) => { if (!cancelled) setIrrSweep(rows); },
      (err) => { console.warn("[sens-chart] irradiance curve failed", err?.message); if (!cancelled) { setIrrSweep([]); setIrrFailed(true); } },
    );
    Promise.all([fuelP, irrP]).then(() => {
      if (cancelled) return;
      setLoading(false);
      console.debug(`[sens-chart] sweep done in ${Date.now() - t0} ms (${calls} /configurator/run calls)`);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sweepKey]);

  const chartData = useMemo(() => {
    // Merge on % delta axis so both series can share one chart
    const merged = PCTS.map((p) => ({
      pct: p,
      fuel: fuelSweep.find((r) => r.pct === p)?.lcoe,
      irr: irrSweep.find((r) => r.pct === p)?.lcoe,
    }));
    return merged;
  }, [fuelSweep, irrSweep]);

  const currentFuelPct = baselineFuel ? ((currentFuel - baselineFuel) / baselineFuel) * 100 : 0;
  const currentIrrPct = baselineIrr ? ((currentIrr - baselineIrr) / baselineIrr) * 100 : 0;
  const currentFuelLcoe = interpolate(chartData, "fuel", currentFuelPct);
  const currentIrrLcoe = interpolate(chartData, "irr", currentIrrPct);

  return (
    <div className="p-4 rounded-md" style={{ background: "var(--paper)", border: "1px solid var(--hairline)" }} data-testid="sensitivity-chart">
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="overline" style={{ fontSize: "0.62rem" }}>{t("sens.chart.title")}</div>
          <p className="text-xs mt-1" style={{ color: "var(--muted)" }}>{t("sens.chart.desc")}</p>
        </div>
        {loading && <span className="text-xs font-mono" style={{ color: "var(--muted)" }}>{t("sens.chart.loading")}</span>}
        {!loading && fuelFailed && (!solar || irrFailed) && (
          <span className="text-xs" style={{ color: "var(--muted)" }} data-testid="sens-chart-load-failed">
            {fr
              ? "Impossible de charger cette comparaison. Rechargez la page pour réessayer."
              : "Couldn't load this comparison. Reload the page to try again."}
          </span>
        )}
        {!loading && solar && fuelFailed !== irrFailed && (
          <span className="text-xs" style={{ color: "var(--muted)" }} data-testid={fuelFailed ? "sens-chart-fuel-failed" : "sens-chart-irr-failed"}>
            {fuelFailed
              ? (fr ? "Impossible de charger la courbe du prix du combustible. Rechargez la page pour réessayer." : "Couldn't load the fuel price curve. Reload the page to try again.")
              : (fr ? "Impossible de charger la courbe d'irradiance. Rechargez la page pour réessayer." : "Couldn't load the irradiance curve. Reload the page to try again.")}
          </span>
        )}
      </div>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <LineChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid stroke="#e2e7e2" strokeDasharray="3 3" />
            <XAxis
              dataKey="pct"
              type="number"
              domain={[-30, 30]}
              ticks={[-30, -20, -10, 0, 10, 20, 30]}
              allowDecimals={false}
              stroke="#6e7a74"
              style={{ fontSize: 11 }}
              tickFormatter={(v) => `${v > 0 ? "+" : ""}${v}${getLocale() === "fr" ? "\u00a0" : ""}%`}
              label={{ value: t("sens.chart.xaxis"), position: "insideBottom", offset: -2, style: { fontSize: 10, fill: "#6e7a74" } }}
            />
            <YAxis
              stroke="#6e7a74"
              style={{ fontSize: 11 }}
              tickFormatter={(v) => `${money(v, 2, 2)}`}
              // §8.7 — LCOE is never negative; the axis must not imply it is.
              domain={[0, "auto"]}
            />
            <Tooltip
              formatter={(v, name) => [`${perUnit(v, "kWh", 4)}`, name]}
              labelFormatter={(v) => `${v >= 0 ? "+" : ""}${v}${getLocale() === "fr" ? "\u00a0" : ""}% ${t("sens.chart.tooltip_axis")}`}
              contentStyle={{ background: "#fff", border: "1px solid var(--hairline)", borderRadius: 6 }}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Line type="monotone" dataKey="fuel" name={t(axis.legendKey)} stroke="#F6611C" strokeWidth={2} dot={{ r: 3 }} />
            {solar && <Line type="monotone" dataKey="irr" name={t("sens.chart.legend.irr")} stroke="#1A6F2B" strokeWidth={2} dot={{ r: 3 }} />}
            {currentFuelLcoe != null && (
              <ReferenceDot
                x={Math.max(-30, Math.min(30, currentFuelPct))}
                y={currentFuelLcoe}
                r={6}
                fill="#F6611C"
                stroke="#fff"
                strokeWidth={2}
                ifOverflow="extendDomain"
                isFront
              />
            )}
            {solar && currentIrrLcoe != null && (
              <ReferenceDot
                x={Math.max(-30, Math.min(30, currentIrrPct))}
                y={currentIrrLcoe}
                r={6}
                fill="#1A6F2B"
                stroke="#fff"
                strokeWidth={2}
                ifOverflow="extendDomain"
                isFront
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
      {!solar && (
        <p className="text-xs mt-2" style={{ color: "var(--muted)" }} data-testid="sens-chart-no-solar">{t("sens.chart.no_solar")}</p>
      )}
      <div className="text-xs mt-2 font-mono" style={{ color: "var(--muted)" }}>
        {t("sens.chart.baseline_prefix")} <span style={{ color: "var(--charcoal)" }}>{perUnit(baselineLcoe, "kWh", 4)}</span> {t("sens.chart.baseline_suffix_fuel")} {num(baselineFuel, 2)}·{t("sens.chart.baseline_suffix_irr")} {num(baselineIrr, 1)}
      </div>
    </div>
  );
};

function interpolate(data, key, x) {
  // Piece-wise linear interpolation of `key` at position `x` (%). Returns null when out of range.
  const pts = data.filter((d) => d[key] !== undefined).map((d) => ({ x: d.pct, y: d[key] }));
  if (pts.length < 2) return null;
  if (x <= pts[0].x) return pts[0].y;
  if (x >= pts[pts.length - 1].x) return pts[pts.length - 1].y;
  for (let i = 0; i < pts.length - 1; i++) {
    if (x >= pts[i].x && x <= pts[i + 1].x) {
      const ratio = (x - pts[i].x) / (pts[i + 1].x - pts[i].x);
      return pts[i].y + ratio * (pts[i + 1].y - pts[i].y);
    }
  }
  return null;
}
